import React from "react";
import PropTypes from "prop-types";
import styles from "./StatsLineGraph.module.css";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export function StatsLineGraph({
  data = [],
  lines,
  xKey = "label",
  rangeLabel = "All time, by weekday",
  yTicks,
  yTickFormatter,
}) {
  const calculatedTotal = React.useMemo(() => {
    let sum = 0;

    data.forEach((row) => {
      lines.forEach((line) => {
        const value = Number(row[line.key]);
        if (!Number.isNaN(value)) {
          sum += value;
        }
      });
    });

    const totalDollars = sum;

    return totalDollars.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }, [data, lines]);

  const renderLegend = (props) => {
    const { payload } = props;
    if (!payload || !payload.length) return null;

    return (
      <div className={styles.legend}>
        {payload.map((entry) => (
          <div key={entry.dataKey} className={styles.legendItem}>
            <span
              className={styles.colorBox}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendLabel}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const yDomain =
    yTicks && yTicks.length
      ? [yTicks[0], yTicks[yTicks.length - 1]]
      : ["auto", "auto"];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <span className={styles.rangeButton}>{rangeLabel}</span>
          <div className={styles.total}>{calculatedTotal}</div>
        </div>
      </div>

      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />

            <XAxis
              dataKey={xKey}
              tickLine={false}
              axisLine={false}
              tickMargin={10}
            />

            <YAxis
              tickLine={false}
              axisLine={false}
              ticks={yTicks}
              domain={yDomain}
              tickFormatter={yTickFormatter}
            />

            <Tooltip />
            <Legend content={renderLegend} />

            {lines.map((line) => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                stroke={line.color}
                strokeWidth={2}
                dot={false}
                name={line.label || line.key}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

StatsLineGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  xKey: PropTypes.string,
  rangeLabel: PropTypes.string,
  yTicks: PropTypes.arrayOf(PropTypes.number),
  yTickFormatter: PropTypes.func,
};
